import { useEffect, useMemo, useState } from "react";
import { useSoftphone } from "../context/SoftphoneContext";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

// ---------------------------------------------------------------------------
// Dial Pad helpers
// ---------------------------------------------------------------------------

const KEYS: { d: string; sub?: string }[] = [
  { d: "1" }, { d: "2", sub: "ABC" }, { d: "3", sub: "DEF" },
  { d: "4", sub: "GHI" }, { d: "5", sub: "JKL" }, { d: "6", sub: "MNO" },
  { d: "7", sub: "PQRS" }, { d: "8", sub: "TUV" }, { d: "9", sub: "WXYZ" },
  { d: "*" }, { d: "0", sub: "+" }, { d: "#" },
];

function formatDialPadPhone(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return `+${d.slice(0, 1)} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 11)}`;
}

function toE164(raw: string): string | null {
  const d = raw.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (raw.startsWith("+") && d.length >= 10) return `+${d}`;
  return null;
}

interface EnrolledUser {
  id: string;
  full_name: string | null;
  email: string | null;
  softphone_last_seen: string | null;
}

type Liveness = "live" | "recent" | "offline" | "never";

function classifyLastSeen(lastSeen: string | null): Liveness {
  if (!lastSeen) return "never";
  const ageMs = Date.now() - new Date(lastSeen).getTime();
  if (ageMs < 60_000) return "live";
  if (ageMs < 5 * 60_000) return "recent";
  return "offline";
}

function timeAgo(lastSeen: string | null): string {
  if (!lastSeen) return "never";
  const ageMs = Date.now() - new Date(lastSeen).getTime();
  const ageSec = Math.floor(ageMs / 1000);
  if (ageSec < 5) return "just now";
  if (ageSec < 60) return `${ageSec}s ago`;
  const ageMin = Math.floor(ageSec / 60);
  if (ageMin < 60) return `${ageMin}m ago`;
  const ageHr = Math.floor(ageMin / 60);
  if (ageHr < 24) return `${ageHr}h ago`;
  return `${Math.floor(ageHr / 24)}d ago`;
}

/**
 * Softphone settings + test page.
 *
 * Three modes:
 *   • Enrolled user (Hennie): full control panel — status, Test Call button,
 *     setup instructions, inbound info.
 *   • Super-user (admin): read-only monitoring view of every enrolled user
 *     with heartbeat-based liveness + the same setup instructions Hennie
 *     sees, so admins can guide him through troubleshooting.
 *   • Anyone else: "not enabled" message.
 */
export default function SoftphonePage() {
  const { member, isSuperUser } = useAuth();
  const sp = useSoftphone();
  const [testing, setTesting] = useState(false);

  // v9: all active team members are enrolled — no allowlist.
  const isSelfEnrolled = !!member;
  const showAdminView = !isSelfEnrolled && isSuperUser;

  // Admin: poll team_roster every 20s for enrolled users' heartbeats.
  const [enrolled, setEnrolled] = useState<EnrolledUser[]>([]);
  useEffect(() => {
    if (!showAdminView) return;
    let cancelled = false;
    async function load() {
      // Fetch all active team members (allowlist removed in v9).
      const { data, error } = await supabase
        .from("team_roster")
        .select("id, full_name, email, softphone_last_seen")
        .eq("is_active", true)
        .not("softphone_last_seen", "is", null);
      if (cancelled) return;
      if (!error && data) setEnrolled(data as EnrolledUser[]);
    }
    void load();
    const interval = setInterval(load, 20_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [showAdminView]);

  async function handleTestCall() {
    if (testing) return;
    setTesting(true);
    try {
      await sp.call({
        phoneNumber: "+15005550006",
        displayName: "Twilio Echo Test",
        tag: "echo_test",
      });
    } catch (err) {
      alert("Test call failed: " + (err as Error).message);
    } finally {
      setTesting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Not enabled — non-admin, non-enrolled
  // -------------------------------------------------------------------------
  if (!isSelfEnrolled && !isSuperUser) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-bardin-ink mb-2">Softphone</h1>
        <p className="text-slate-600">
          Browser softphone isn't enabled for this account
          {member?.full_name ? ` (${member.full_name})` : ""}. If you should have access,
          ping Greg.
        </p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Admin read-only mirror
  // -------------------------------------------------------------------------
  if (showAdminView) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-bardin-ink">Softphone</h1>
            <span className="text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-500">
              View only
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Read-only monitoring of enrolled users. You're not on the call path —
            this view exists so you can help troubleshoot.
          </p>
        </div>

        {/* Enrolled users */}
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-base font-semibold text-bardin-ink mb-3">Enrolled users</h2>
          {enrolled.length === 0 ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {enrolled.map((u) => {
                const live = classifyLastSeen(u.softphone_last_seen);
                const dotColor =
                  live === "live" ? "bg-emerald-500 animate-pulse"
                  : live === "recent" ? "bg-amber-400"
                  : "bg-slate-300";
                const labelColor =
                  live === "live" ? "text-emerald-700"
                  : live === "recent" ? "text-amber-700"
                  : "text-slate-500";
                const labelText =
                  live === "live" ? "Live"
                  : live === "recent" ? `Idle · ${timeAgo(u.softphone_last_seen)}`
                  : live === "offline" ? `Offline · ${timeAgo(u.softphone_last_seen)}`
                  : "Never connected";
                return (
                  <li key={u.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${dotColor}`} aria-hidden />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-bardin-ink truncate">
                          {u.full_name ?? "(no name)"}
                        </div>
                        {u.email && (
                          <div className="text-xs text-slate-500 truncate">{u.email}</div>
                        )}
                      </div>
                    </div>
                    <span className={`text-sm font-medium ${labelColor} shrink-0 ml-3`}>
                      {labelText}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Liveness key */}
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
          <h2 className="text-base font-semibold text-bardin-ink">What the labels mean</h2>
          <ul className="text-sm text-slate-700 space-y-1.5">
            <li><span className="text-emerald-700 font-semibold">Live</span> — heartbeat within the last 60s. Calls ring on their laptop. Inbound routing prefers them.</li>
            <li><span className="text-amber-700 font-semibold">Idle · Xm ago</span> — heartbeat 1–5 min old. Tab probably backgrounded. Inbound calls would still ring their laptop for a few more minutes, then fall through.</li>
            <li><span className="text-slate-500 font-semibold">Offline</span> — heartbeat older than 5 min. Laptop closed/asleep. Inbound calls go to voicemail (no SA cell to fall back to for Hennie).</li>
            <li><span className="text-slate-500 font-semibold">Never connected</span> — user is allowlisted but hasn't loaded the portal in a softphone-capable session yet.</li>
          </ul>
        </section>

        {/* Setup instructions admins can walk users through */}
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
          <h2 className="text-base font-semibold text-bardin-ink">Troubleshooting checklist</h2>
          <p className="text-xs text-slate-500">
            What you'd ask the user to verify if their softphone is offline or stuck.
          </p>
          <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
            <li>Are they using <span className="font-medium">Chrome</span> on Mac? (Safari has been flakier on reconnects.)</li>
            <li>Did they click <span className="font-medium">Allow</span> on the Chrome microphone prompt?</li>
            <li>Is Chrome enabled in <span className="font-medium">macOS System Settings → Privacy &amp; Security → Microphone</span>?</li>
            <li>Is their <span className="font-medium">Razer / USB headset plugged in</span> and selected as the input/output in System Settings → Sound?</li>
            <li>Is at least one portal tab open? Closing all portal tabs kills the heartbeat.</li>
            <li>Have them open <span className="font-medium">/softphone</span> and run a <span className="font-medium">Test Call</span>. They should hear their voice played back.</li>
          </ol>
        </section>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Enrolled user — full control panel
  // -------------------------------------------------------------------------
  const statusMeta: Record<typeof sp.status, { label: string; color: string; bg: string }> = {
    disabled:    { label: "Disabled",         color: "text-slate-500",  bg: "bg-slate-100" },
    registering: { label: "Connecting…",      color: "text-amber-700",  bg: "bg-amber-50" },
    ready:       { label: "Ready",            color: "text-emerald-700", bg: "bg-emerald-50" },
    connecting:  { label: "Dialing…",         color: "text-amber-700",  bg: "bg-amber-50" },
    ringing:     { label: "Ringing…",         color: "text-amber-700",  bg: "bg-amber-50" },
    "in-call":   { label: "On a call",        color: "text-emerald-700", bg: "bg-emerald-50" },
    ending:      { label: "Ending…",          color: "text-slate-500",  bg: "bg-slate-50" },
    error:       { label: sp.error ?? "Error", color: "text-red-700",   bg: "bg-red-50" },
  };
  const meta = statusMeta[sp.status];

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-bardin-ink">Softphone</h1>
        <p className="text-sm text-slate-500 mt-1">
          Make and receive calls through your laptop. Calls go out from the Bardin
          number <span className="font-medium text-slate-700">(470) 823-8816</span>.
        </p>
      </div>

      <section className={`rounded-xl border border-slate-200 ${meta.bg} p-4`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</div>
            <div className={`text-lg font-semibold ${meta.color}`}>{meta.label}</div>
            {sp.identity && (
              <div className="text-xs text-slate-500 mt-1">Signed in as {sp.identity}</div>
            )}
          </div>
          {sp.status === "error" && (
            <button
              onClick={sp.reinit}
              className="px-3 py-1.5 rounded-lg bg-bardin-ink text-white text-xs font-semibold hover:bg-bardin-ink/80 transition"
            >
              Retry
            </button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-bardin-ink">Test Call</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Calls Twilio's echo service. Say something — it'll repeat your audio back.
            Confirms mic, speakers, network, and the bridge all work end-to-end.
          </p>
        </div>
        <button
          onClick={handleTestCall}
          disabled={testing || sp.status !== "ready"}
          className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          {testing ? "Calling echo…" : "Start Test Call"}
        </button>
        {sp.status !== "ready" && (
          <p className="text-xs text-slate-500">
            Test call disabled — softphone is{" "}
            <span className="font-medium">{meta.label.toLowerCase()}</span>. Wait for{" "}
            <span className="font-medium text-emerald-700">Ready</span> before testing.
          </p>
        )}
      </section>

      {/* ------ Dial Pad — make real calls via browser softphone ------ */}
      <SoftphoneDialPad />

      <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
        <h2 className="text-base font-semibold text-bardin-ink">First-time setup</h2>
        <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
          <li>
            <span className="font-medium">Use Chrome.</span> Safari technically works but
            has been flakier on call reconnects. Open this page in Chrome on your Mac.
          </li>
          <li>
            <span className="font-medium">Allow microphone access.</span> Chrome will prompt
            once. If you missed it, open System Settings → Privacy &amp; Security →
            Microphone, and toggle Chrome on.
          </li>
          <li>
            <span className="font-medium">Plug in your Razer via USB cable.</span> Wired
            only — don't switch to Bluetooth/wireless mid-day. Wireless battery dying
            mid-call is the one preventable failure mode.
          </li>
          <li>
            <span className="font-medium">Run a Test Call.</span> If you hear yourself
            played back, you're good. If not, check System Settings → Sound → Input
            and select your headset.
          </li>
          <li>
            <span className="font-medium">Make a real call.</span> From any deal page, click
            the Call button. You'll get a confirmation prompt — say yes and the call
            rings through your laptop.
          </li>
        </ol>
      </section>

      <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
        <h2 className="text-base font-semibold text-bardin-ink">When homeowners call you</h2>
        <p className="text-sm text-slate-700">
          When a homeowner calls (470) 823-8816, the system rings your laptop directly. If
          your laptop is closed or asleep, the call falls through to voicemail.
        </p>
        <p className="text-xs text-slate-500">
          Keep this tab open (or any portal tab) for inbound calls to reach you.
        </p>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SoftphoneDialPad — dial any number via the browser softphone
// ---------------------------------------------------------------------------

function SoftphoneDialPad() {
  const sp = useSoftphone();
  const [raw, setRaw] = useState("");
  const [dialing, setDialing] = useState(false);
  const [dialStatus, setDialStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const display = useMemo(() => formatDialPadPhone(raw), [raw]);
  const e164 = useMemo(() => toE164(raw), [raw]);
  const canCall = !!e164 && !dialing && sp.status === "ready";

  function press(d: string) {
    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 11 && /\d/.test(d)) return;
    setRaw((r) => r + d);
    setDialStatus(null);
  }

  function backspace() {
    setRaw((r) => r.slice(0, -1));
    setDialStatus(null);
  }

  function clear() {
    setRaw("");
    setDialStatus(null);
  }

  async function handleCall() {
    if (!e164 || !canCall) return;
    setDialing(true);
    setDialStatus(null);
    try {
      await sp.call({
        phoneNumber: e164,
        displayName: display,
        tag: "dial_pad",
      });
      setDialStatus({ kind: "ok", msg: `Calling ${display} — use the call bar at the bottom to mute / hang up.` });
    } catch (err) {
      setDialStatus({ kind: "err", msg: "Call failed: " + (err as Error).message });
    } finally {
      setDialing(false);
    }
  }

  // If there's an active call, show a compact "on call" state instead of the keypad
  const isOnCall = sp.status === "connecting" || sp.status === "ringing" || sp.status === "in-call";

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div>
        <h2 className="text-base font-semibold text-bardin-ink">Dial Pad</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Type a number and call directly from your browser. No cell bridge — audio goes
          through your laptop mic &amp; speakers.
        </p>
      </div>

      {isOnCall ? (
        <div className="text-center py-4">
          <div className="text-sm text-emerald-700 font-medium">
            {sp.status === "in-call" ? "Call in progress" : "Connecting…"}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Use the call bar at the bottom of the screen to mute or hang up.
          </p>
        </div>
      ) : (
        <>
          {/* Number display */}
          <div className="text-center text-2xl sm:text-3xl font-semibold tabular-nums text-bardin-ink min-h-[40px] select-all">
            {display || <span className="text-slate-300">Enter number</span>}
          </div>
          <div className="text-center text-[11px] text-slate-400 h-4">
            {raw && !e164 ? "Enter 10 digits" : ""}
          </div>

          {/* Keypad grid */}
          <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
            {KEYS.map((k) => (
              <button
                key={k.d}
                onClick={() => press(k.d)}
                className="aspect-square rounded-xl bg-slate-50 hover:bg-slate-100 active:bg-slate-200 border border-slate-200 flex flex-col items-center justify-center transition"
                type="button"
              >
                <span className="text-2xl font-semibold text-bardin-ink leading-none">{k.d}</span>
                {k.sub && (
                  <span className="text-[9px] tracking-widest text-slate-400 uppercase mt-0.5">{k.sub}</span>
                )}
              </button>
            ))}
          </div>

          {/* Delete / Clear */}
          <div className="flex gap-2 max-w-xs mx-auto">
            <button
              onClick={backspace}
              disabled={raw.length === 0 || dialing}
              className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition"
              type="button"
            >
              ⌫ Delete
            </button>
            <button
              onClick={clear}
              disabled={raw.length === 0 || dialing}
              className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition"
              type="button"
            >
              Clear
            </button>
          </div>

          {/* Call button */}
          <button
            onClick={handleCall}
            disabled={!canCall}
            className={
              "w-full max-w-xs mx-auto block py-3 rounded-lg text-base font-semibold transition " +
              (canCall
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-slate-200 text-slate-400 cursor-not-allowed")
            }
            type="button"
          >
            {dialing
              ? "Connecting…"
              : `📞 Call ${display || "—"}`}
          </button>

          {sp.status !== "ready" && !dialing && (
            <p className="text-xs text-slate-500 text-center">
              Dial pad disabled — softphone is <span className="font-medium">{sp.status}</span>.
              Wait for <span className="font-medium text-emerald-700">Ready</span>.
            </p>
          )}
        </>
      )}

      {/* Status message */}
      {dialStatus && (
        <div
          className={
            "text-sm rounded-lg px-3 py-2 " +
            (dialStatus.kind === "ok"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-red-50 text-red-700 border border-red-200")
          }
        >
          {dialStatus.msg}
        </div>
      )}
    </section>
  );
}
